'use client'

import { memo, useCallback, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'

import { addEditableFilter, removeEditableFilter } from '../../model/catalogSlice'
import type { TFilterConfig, TFilterOption, TFilterOptionComponents } from '../../model/filterTypes'

interface IFilterOptionProps extends TFilterOption {
    paramsData: boolean
    config: ReturnType<TFilterConfig>
    components: TFilterOptionComponents
}

export const FilterOption = memo(function FilterOption({
    paramsData,
    config,
    title,
    items,
    components: { FilterOptionWrapper, FilterCheckbox, FilterSwitch, FilterNavItem, FilterNumbers }
}: IFilterOptionProps) {
    const dispatch = useAppDispatch()

    const { editableFilter } = useAppSelector(state => state.catalog)

    const currentEditableFilter = useMemo(
        () => editableFilter.find(obj => obj.title === title),
        [editableFilter, title]
    )

    const isOptionActive = useMemo(() => {
        if (currentEditableFilter && currentEditableFilter.items.length > 0) return true
        else if (paramsData) return true
        return false
    }, [currentEditableFilter, paramsData])

    const handleItemActive = useCallback(
        (title: string) => {
            if (currentEditableFilter && currentEditableFilter.items.find(item => item.title === title)) return true
            return false
        },
        [currentEditableFilter, paramsData]
    )

    return (
        <FilterOptionWrapper
            type={config.type}
            title={title}
            active={isOptionActive}
        >
            <p className='catalog__sort-title'>{title}</p>
            {items.map(item => {
                const active = handleItemActive(item.title)

                const filterPayload = { title, item, config }
                const commonProps = {
                    ...item,
                    onClick: () =>
                        active
                            ? dispatch(removeEditableFilter(filterPayload))
                            : dispatch(addEditableFilter(filterPayload)),
                    active,
                    key: item.id
                }

                switch (config.type) {
                    case 'checkbox':
                        return FilterCheckbox && <FilterCheckbox {...commonProps} />

                    case 'switch':
                        return FilterSwitch && <FilterSwitch {...commonProps} />

                    case 'navigation':
                        return FilterNavItem && <FilterNavItem {...commonProps} />

                    case 'numbers':
                        return FilterNumbers && <FilterNumbers {...commonProps} />
                }
            })}
        </FilterOptionWrapper>
    )
})
